import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowRight, MessageCircle, MapPin, Sparkles, ChevronRight } from "lucide-react";
import api from "@/lib/api";
import { formatINR } from "@/lib/utils-format";
import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from "@/components/ui/accordion";
import Seo, { faqSchema, breadcrumbSchema, localBusinessSchema, orgSchema } from "@/components/Seo";
import WhatsAppCTA from "@/components/WhatsAppCTA";

const SITE = "https://signiastylettoix.com";

export default function SeoPage() {
    const { slug } = useParams();
    const navigate = useNavigate();
    const [page, setPage] = useState(null);

    useEffect(() => {
        let cancelled = false;
        api.get(`/seo/pages/${slug}`)
            .then((r) => { if (!cancelled) setPage(r.data); })
            .catch((err) => {
                console.error("SEO page fetch failed:", err);
                if (!cancelled) navigate("/");
            });
        return () => { cancelled = true; };
    }, [slug, navigate]);

    if (!page) return <div className="min-h-[60vh] flex items-center justify-center text-[#4a5568]">Loading…</div>;

    const url = `${SITE}/seo/${page.slug}`;
    const schema = {
        "@context": "https://schema.org",
        "@graph": [
            orgSchema(),
            breadcrumbSchema([
                { name: "Home", url: SITE + "/" },
                { name: page.h1, url },
            ]),
            faqSchema(page.faqs || []),
            ...(page.city ? [localBusinessSchema(page.city, page.schema_type || "LocalBusiness")] : []),
        ],
    };

    return (
        <>
            <Seo title={page.title} description={page.meta_description} canonical={url} schema={schema} />

            {/* Hero */}
            <section className="pt-12 md:pt-20 pb-10 md:pb-16">
                <div className="container-x">
                    <nav className="text-[12.5px] text-[#4a5568] mb-6" aria-label="Breadcrumb">
                        <Link to="/" className="hover:text-[#003E4D]">Home</Link>
                        <span className="mx-2 text-[#9CA3AF]">/</span>
                        <span className="text-[#1a1a1a]">{page.h1}</span>
                    </nav>
                    <p className="eyebrow inline-flex items-center gap-2">
                        {page.city ? <MapPin size={12} /> : <Sparkles size={12} />} {page.eyebrow}
                    </p>
                    <h1 className="font-serif text-[40px] md:text-[60px] xl:text-[68px] leading-[1.05] mt-4 max-w-[920px]" data-testid="seo-h1">{page.h1}</h1>
                    <div
                        className="mt-6 text-[16.5px] md:text-[18px] text-[#4a5568] leading-relaxed max-w-[760px]"
                        dangerouslySetInnerHTML={{ __html: linkify(page.intro) }}
                    />
                    <div className="mt-8 flex flex-wrap items-center gap-3">
                        <WhatsAppCTA
                            message={`Hi, I want the best price for Signia Styletto IX. (Source: ${page.slug})`}
                            sourcePage={`/seo/${page.slug}`}
                            className="btn-whatsapp"
                            data-testid="seo-whatsapp-cta"
                        >
                            <MessageCircle size={16} /> Get Best Price on WhatsApp
                        </WhatsAppCTA>
                        <Link to="/book-trial" className="btn-primary" data-testid="seo-trial-cta">
                            Book Free Trial <ArrowRight size={16} />
                        </Link>
                        <Link to="/products" className="btn-secondary">View all models</Link>
                    </div>
                </div>
            </section>

            {/* Sections */}
            <section className="pb-10 md:pb-16">
                <div className="container-x grid lg:grid-cols-12 gap-10">
                    <article className="lg:col-span-8 prose-stx">
                        {(page.sections || []).map((s, i) => (
                            <div key={i} className="mb-10" data-testid={`seo-section-${i}`}>
                                <h2 className="font-serif text-[28px] md:text-[36px] leading-tight text-[#1a1a1a] mt-2">{s.h2}</h2>
                                <div
                                    className="mt-4 text-[15.5px] md:text-[16px] text-[#4a5568] leading-relaxed"
                                    dangerouslySetInnerHTML={{ __html: linkify(s.body) }}
                                />
                            </div>
                        ))}
                    </article>

                    {/* Sticky sidebar */}
                    <aside className="lg:col-span-4">
                        <div className="lg:sticky lg:top-24 space-y-5">
                            <div className="bg-[#003E4D] text-white rounded-3xl p-7 relative overflow-hidden">
                                <p className="eyebrow !text-[#7DCDD2]">Talk to a specialist</p>
                                <h3 className="font-serif text-[26px] mt-2 leading-tight">Get the best Styletto IX price right now.</h3>
                                <p className="text-[13.5px] text-white/80 mt-3 leading-relaxed">
                                    HearClear audiologists are online — instant pricing, EMI options, free home trial.
                                </p>
                                <WhatsAppCTA
                                    message={`Hi, I want the best price for Signia Styletto IX. (${page.slug})`}
                                    sourcePage={`/seo/${page.slug}-sidebar`}
                                    className="btn-whatsapp w-full justify-center mt-5"
                                >
                                    <MessageCircle size={16} /> Get Best Price
                                </WhatsAppCTA>
                                <Link to="/book-trial" className="btn-primary w-full justify-center mt-3 !bg-white !text-[#003E4D] hover:!bg-white/90">
                                    Book Free Trial <ArrowRight size={16} />
                                </Link>
                            </div>

                            {(page.related_products || []).length > 0 && (
                                <div className="bg-white rounded-3xl p-6 border border-black/5" data-testid="seo-related-products">
                                    <p className="eyebrow">Featured Models</p>
                                    <ul className="mt-4 space-y-3">
                                        {page.related_products.map((p) => (
                                            <li key={p.slug}>
                                                <Link to={`/products/${p.slug}`} className="flex items-center gap-3 group">
                                                    <img src={p.image} alt={p.name} className="w-14 h-14 object-contain bg-[#f3f4f6] rounded-lg" />
                                                    <div className="flex-1 min-w-0">
                                                        <p className="text-[14px] font-medium text-[#1a1a1a] group-hover:text-[#003E4D] truncate">{p.short_name}</p>
                                                        <p className="text-[12px] text-[#4a5568]">{formatINR(p.price)}</p>
                                                    </div>
                                                    <ChevronRight size={16} className="text-[#9CA3AF] group-hover:text-[#003E4D]" />
                                                </Link>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}

                            {(page.related_pages || []).length > 0 && (
                                <div className="bg-white rounded-3xl p-6 border border-black/5 mt-4" data-testid="seo-related-pages">
                                    <p className="eyebrow">Explore Nearby</p>
                                    <ul className="mt-4 space-y-2">
                                        {page.related_pages.slice(0, 12).map((slug) => {
                                            const label = slug.replace("signia-styletto-", "").replace("pin-", "PIN ").replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
                                            return (
                                                <li key={slug}>
                                                    <Link to={`/seo/${slug}`} className="flex items-center justify-between group text-[13px]">
                                                        <span className="text-[#4a5568] group-hover:text-[#003E4D] truncate">{label}</span>
                                                        <ChevronRight size={14} className="text-[#9CA3AF] group-hover:text-[#003E4D] flex-shrink-0" />
                                                    </Link>
                                                </li>
                                            );
                                        })}
                                    </ul>
                                </div>
                            )}
                        </div>
                    </aside>
                </div>
            </section>

            {/* FAQs */}
            {(page.faqs || []).length > 0 && (
                <section className="section bg-[#f8f9fa]" data-testid="seo-faq">
                    <div className="container-x grid lg:grid-cols-12 gap-12">
                        <div className="lg:col-span-4">
                            <p className="eyebrow">Frequently Asked</p>
                            <h2 className="font-serif text-[36px] md:text-[48px] leading-tight mt-3">Quick answers.</h2>
                        </div>
                        <div className="lg:col-span-8">
                            <Accordion type="single" collapsible className="w-full">
                                {page.faqs.map((f, i) => (
                                    <AccordionItem key={i} value={`item-${i}`} className="border-b border-[#E5E7EB]">
                                        <AccordionTrigger className="text-left font-serif text-[20px] md:text-[22px] py-5 hover:no-underline">
                                            {f.q}
                                        </AccordionTrigger>
                                        <AccordionContent className="text-[15px] text-[#4a5568] leading-relaxed pb-5 pr-4">
                                            {f.a}
                                        </AccordionContent>
                                    </AccordionItem>
                                ))}
                            </Accordion>
                        </div>
                    </div>
                </section>
            )}
        </>
    );
}

// Internal-link helper — lightweight
function linkify(html) {
    if (!html) return "";
    return html
        .replace(/\bStyletto IX 1IX\b/g, '<a class="text-[#003E4D] underline-offset-4 hover:underline" href="/products/signia-styletto-1ix">Styletto 1IX</a>')
        .replace(/\bStyletto 1IX\b/g, '<a class="text-[#003E4D] underline-offset-4 hover:underline" href="/products/signia-styletto-1ix">Styletto 1IX</a>')
        .replace(/\bStyletto 2IX\b/g, '<a class="text-[#003E4D] underline-offset-4 hover:underline" href="/products/kit-styletto-2ix">Styletto 2IX</a>')
        .replace(/\bStyletto 3IX\b/g, '<a class="text-[#003E4D] underline-offset-4 hover:underline" href="/products/kit-styletto-3ix">Styletto 3IX</a>')
        .replace(/\bStyletto 5IX\b/g, '<a class="text-[#003E4D] underline-offset-4 hover:underline" href="/products/kit-styletto-5ix">Styletto 5IX</a>')
        .replace(/\bStyletto 7IX\b/g, '<a class="text-[#003E4D] underline-offset-4 hover:underline" href="/products/kit-styletto-7ix">Styletto 7IX</a>');
}
